/**
 * Thin wrapper around the shared {@link EventForm} (variant 'dialog'): the event creation
 * dialog ported from the Syncra schedule mockup. This module keeps the Radix Dialog shell
 * (content chrome, header title/description) and the module's public surface (props,
 * {@link EventType}, {@link slugifyAdRoom}); the type cards, the fields, the AD rooms picker,
 * the draft state and the create flow live in EventForm.
 */

import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { EventForm } from './EventForm';
import type { DialogEventFormProps } from './EventForm';

export type { EventType } from './EventForm';
export { slugifyAdRoom } from './EventForm';

/** The dialog's public props: exactly the shared form's dialog member. */
export type ScheduleDialogProps = DialogEventFormProps;

export function ScheduleDialog({
  open,
  onOpenChange,
  token,
  initialType = 'meeting',
  onCreated,
}: ScheduleDialogProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] scroll-thin overflow-y-auto border-border bg-card p-0 sm:max-w-[560px]">
        <DialogHeader className="border-b border-border px-6 pb-4 pt-5">
          <DialogTitle className="text-[17px] font-semibold tracking-tight text-foreground">
            {t('calendar.schedule.title')}
          </DialogTitle>
          <DialogDescription className="text-[13px] text-muted-foreground">
            {t('calendar.schedule.description')}
          </DialogDescription>
        </DialogHeader>

        <EventForm
          variant="dialog"
          open={open}
          onOpenChange={onOpenChange}
          token={token}
          initialType={initialType}
          onCreated={onCreated}
        />
      </DialogContent>
    </Dialog>
  );
}
